import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { setSentryUser, clearSentryUser } from '@/lib/sentry'
import type { UserTenant, UserRole } from '@/types/auth'

interface AuthContextType {
  user: User | null
  session: Session | null
  currentTenant: UserTenant | null
  tenants: UserTenant[]
  loading: boolean
  tenantsLoading: boolean
  tenantsFetched: boolean
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
  switchTenant: (tenantId: string) => void
  hasRole: (roles: UserRole | UserRole[]) => boolean
  hasGlobalRole: (roles: UserRole | UserRole[]) => boolean
  refreshTenants: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [tenants, setTenants] = useState<UserTenant[]>([])
  const [currentTenant, setCurrentTenant] = useState<UserTenant | null>(null)
  const [loading, setLoading] = useState(true)
  const [tenantsLoading, setTenantsLoading] = useState(false)
  const [tenantsFetched, setTenantsFetched] = useState(false)

  // Fetch user's tenants - called outside of onAuthStateChange
  const fetchUserTenants = useCallback(async (userId: string): Promise<UserTenant[]> => {
    const { data, error } = await supabase
      .from('user_tenants')
      .select(`
        user_id,
        tenant_id,
        role,
        is_primary,
        tenant:tenants (
          id,
          name,
          slug,
          schema_name,
          domain,
          logo_url,
          status,
          settings,
          created_at
        )
      `)
      .eq('user_id', userId)

    if (error) {
      console.error('Error fetching tenants:', error)
      return []
    }

    if (!data) return []

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const userTenants: UserTenant[] = data.map((ut: any) => ({
      user_id: ut.user_id,
      tenant_id: ut.tenant_id,
      role: ut.role as UserRole,
      is_primary: ut.is_primary,
      tenant: ut.tenant as UserTenant['tenant'],
    }))

    return userTenants
  }, [])

  // Refresh tenants - can be called after linking user to tenant
  const refreshTenants = useCallback(async () => {
    if (!user) return
    const userTenants = await fetchUserTenants(user.id)
    setTenants(userTenants)
    const primary = userTenants.find((t) => t.is_primary) || userTenants[0]
    setCurrentTenant(primary || null)
  }, [user, fetchUserTenants])

  // Initialize auth state
  useEffect(() => {
    let mounted = true

    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!mounted) return
      setSession(session)
      setUser(session?.user ?? null)

      // Fetch tenants BEFORE setting loading to false
      if (session?.user) {
        const userTenants = await fetchUserTenants(session.user.id)
        if (!mounted) return
        setTenants(userTenants)
        const primary = userTenants.find((t) => t.is_primary) || userTenants[0]
        setCurrentTenant(primary || null)
        setTenantsFetched(true)

        // Set Sentry user context
        setSentryUser({
          id: session.user.id,
          email: session.user.email,
          tenantId: primary?.tenant_id,
        })
      }

      setLoading(false)
    })

    // Listen for auth changes
    // IMPORTANT: Don't make async Supabase calls directly in this callback
    // It causes deadlocks. Use setTimeout to defer them.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return

      setSession(session)
      setUser(session?.user ?? null)

      if (session?.user) {
        // Defer async operations to avoid deadlock
        // See: https://github.com/supabase/auth-js/issues/762
        setTenantsLoading(true)
        setTimeout(() => {
          fetchUserTenants(session.user.id).then((userTenants) => {
            if (!mounted) return
            setTenants(userTenants)
            const primary = userTenants.find((t) => t.is_primary) || userTenants[0]
            setCurrentTenant(primary || null)
            setTenantsFetched(true)
            setTenantsLoading(false)

            // Set Sentry user context
            setSentryUser({
              id: session.user.id,
              email: session.user.email,
              tenantId: primary?.tenant_id,
            })
          })
        }, 0)
      } else {
        setTenants([])
        setCurrentTenant(null)
        setTenantsLoading(false)
        clearSentryUser()
      }
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [fetchUserTenants])

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { error: error as Error | null }
  }

  const signOut = async () => {
    setUser(null)
    setSession(null)
    setTenants([])
    setCurrentTenant(null)
    setTenantsFetched(false)
    clearSentryUser()
    await supabase.auth.signOut()
  }

  const switchTenant = (tenantId: string) => {
    const tenant = tenants.find((t) => t.tenant_id === tenantId)
    if (tenant) {
      setCurrentTenant(tenant)
    }
  }

  const hasRole = (roles: UserRole | UserRole[]): boolean => {
    if (!currentTenant) return false
    const roleArray = Array.isArray(roles) ? roles : [roles]
    return roleArray.includes(currentTenant.role)
  }

  const hasGlobalRole = useCallback((roles: UserRole | UserRole[]): boolean => {
    const roleArray = Array.isArray(roles) ? roles : [roles]
    return tenants.some(t => roleArray.includes(t.role))
  }, [tenants])

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        currentTenant,
        tenants,
        loading,
        tenantsLoading,
        tenantsFetched,
        signIn,
        signOut,
        switchTenant,
        hasRole,
        hasGlobalRole,
        refreshTenants,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
